"use client";
import { PiArrowRight, PiCircleNotch, PiMagicWand } from "react-icons/pi";
import { Universe, UniverseSelect } from "../app/UniverseSelect";
import { useActionState, useContext, useEffect, useRef, useState } from "react";
import Link from "next/link";
import { getCookie } from "@cappelendamm/cookie";
import {
  COOKIE_INTERNAL_USER_OR_BOT,
  INTERNAL_USER_DETECTION,
} from "@/routing";

import { Button, Drawer, Typography } from "@cappelendamm/design-system/react";
import classNames from "classnames";
import { revalidatePathAction } from "./actions";
import { usePathname } from "next/navigation";
import { filterDuplicates } from "@cappelendamm/utils/array";
import { TrackingContext } from "@/tracking/TrackingContext";

export const ToolBarSlider = ({ universes }: { universes: Universe[] }) => {
  const [isVisible, setIsVisible] = useState<boolean>(false);
  const pathname = usePathname();
  const [revalidateState, revalidateAction, revalidateIsPending] =
    useActionState(revalidatePathAction, null);
  const { events } = useContext(TrackingContext);

  // Simple in-memory log of admin actions
  const [logLines, setLogLines] = useState<string[]>([]);

  useEffect(() => {
    const timestamp = new Date().toLocaleTimeString();
    setLogLines((prev) =>
      filterDuplicates([
        ...prev,
        ...events.map(({ eventName }) => `${timestamp} — ${eventName}`),
      ]),
    );
  }, [events]);

  const logRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    const botCookie = getCookie(COOKIE_INTERNAL_USER_OR_BOT);
    const isBot = [
      ...INTERNAL_USER_DETECTION.HEADERS,
      ...INTERNAL_USER_DETECTION.BOTS,
    ].includes(botCookie as "bot" | "cada-playwright-runner");
    setIsVisible(!isBot);
  }, []);
  const ref = useRef<HTMLDialogElement>(null);

  const isOpen = ref.current?.open ?? false;

  // Append revalidate messages to the log when they change
  useEffect(() => {
    if (revalidateState?.message) {
      const timestamp = new Date().toLocaleTimeString();
      setLogLines((prev) =>
        filterDuplicates([
          ...prev,
          `${timestamp} — revalidate: ${revalidateState.message}`,
        ]),
      );
    }
  }, [revalidateState?.message]);

  // Keep textarea scrolled to bottom on new logs
  useEffect(() => {
    if (logRef.current) {
      logRef.current.scrollTop = logRef.current.scrollHeight;
    }
  }, [logLines]);

  if (!isVisible) return null;
  return (
    <>
      <button
        className="fixed right-4 bottom-4 z-50 flex h-8 w-8 items-center justify-center rounded-full bg-status-warning-500 text-black shadow-2xl"
        onClick={() => {
          if (ref.current && !ref.current?.open) ref.current.showModal();
          else ref.current?.close();
        }}
      >
        <PiArrowRight
          size={24}
          className={classNames("transition-all", { "rotate-180": isOpen })}
        />
      </button>
      <Drawer
        ref={ref}
        variant="right"
        className="fixed grid h-full gap-4"
        data-theme="admin"
      >
        <div className="grid gap-2 self-start">
          <Typography variant="title-lg">Log</Typography>
          <textarea
            ref={logRef}
            disabled
            className="border-outline-300 text-foreground-900 h-40 w-full resize-none rounded border bg-surface-50 p-2 font-mono text-xs"
            value={logLines.join("\n")}
            placeholder="No logs yet"
          />
        </div>
        <UniverseSelect universes={universes} />
        <div className="grid content-end gap-4">
          <Link
            href="/admin"
            title="Admin page"
            className="flex items-center gap-2"
          >
            <PiMagicWand
              className="rounded-full p-1 hover:bg-white/75"
              size={32}
            />{" "}
            Admin panel
          </Link>
          <form action={revalidateAction}>
            <input type="hidden" name="path" value={pathname} />
            <Button type="submit" className="w-full">
              {revalidateIsPending ? (
                <PiCircleNotch className="animate-spin" />
              ) : (
                `Revalidate current page`
              )}
            </Button>
          </form>
        </div>
      </Drawer>
    </>
  );
};
